import React from 'react';
import { RouteComponentProps as ReactRouterComponentProps, Redirect, RedirectProps, Route, Switch, useLocation } from 'react-router-dom';
import { useUser } from '../hooks/auth';
import { routePaths } from '../../routePaths';
import { useGetInitInfoQuery } from '../../generated/graphql';
import { checkAccess, checkVenueTypeAccess } from '../helpers';
import SEO from '../../components/SEO';
import { ProtectedRouteProps } from './types';

const ProtectedRouteComponent = ({
    component,
    redirectRoute,
    permissions,
    allowedVenueTypes,
    title,
    ...rest
}: ProtectedRouteProps) => {
    const { user: { employeeId, role, venueType } } = useUser();
    const { pathname } = useLocation();
    const { data } = useGetInitInfoQuery({ fetchPolicy: 'cache-only' });

    const Component = component;
    const currentPathname = rest.location?.pathname;
    const employeeProfilePathname = `${routePaths.employee}/${employeeId}`;
    const finalRedirectRoute = redirectRoute || employeeProfilePathname;
    const isCurrentPathnameEmployeeProfile = currentPathname === employeeProfilePathname;

    const hasAccess = data
        ? checkVenueTypeAccess(allowedVenueTypes, venueType) && checkAccess(permissions, data.employeePermissions)
        : false || isCurrentPathnameEmployeeProfile;

    const redirectTo: RedirectProps['to'] = {
        pathname: finalRedirectRoute,
        state: { referrer: pathname }
    };

    if (!role || !hasAccess) {
        return <Redirect to={redirectTo} />;
    }

    return (
        <Route
            {...rest}
            render={(routeProps: ReactRouterComponentProps) => (
                <>
                    <SEO title={title} />
                    <Component {...routeProps} />
                </>
            )}
        />
    );
};

const ProtectedRoute = ({
    childrenRoutes,
    scopeProvider,
    ...rest
}: ProtectedRouteProps) => {
    const Wrapper = scopeProvider || React.Fragment;

    return (
        <Wrapper>
            <ProtectedRouteComponent {...rest} />
            {childrenRoutes
                    ? (
                        <Switch>
                            {childrenRoutes.map(props => (
                                <ProtectedRoute
                                    key={props.path}
                                    {...props}
                                />
                            ))}
                        </Switch>
                    ) : null}
        </Wrapper>
    );
};

export default ProtectedRoute;
